'use client';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { Building2, Users, BookOpen, MapPin } from 'lucide-react';
import { useBlogs, useCreateContactSubmission, useKnowledgeHub } from '@/hooks/useApi';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { resolveImageUrl } from '@/lib/image';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { slugifyRouteValue } from '@/lib/slug';

const PhoneInput = dynamic(
    () => import('react-international-phone').then((mod) => mod.PhoneInput),
    { ssr: false }
);

export const Home: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { data: blogsData, isLoading } = useBlogs(1, 3);
    const { data: knowledgeData, isLoading: isKnowledgeLoading } = useKnowledgeHub(1, 3);
    const heroImages = ['/hero-1.jpeg', '/hero-2.jpeg', '/hero-3.jpeg', '/hero-4.jpg', '/hero-5.jpg', '/hero-6.jpeg'];
    const [activeHero, setActiveHero] = useState(0);
    const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
    const [newsletterName, setNewsletterName] = useState('');
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [newsletterError, setNewsletterError] = useState('');
    const [newsletterLanguage, setNewsletterLanguage] = useState<'EN' | 'DE'>('EN');
    const createContactSubmission = useCreateContactSubmission();
    const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [contactError, setContactError] = useState('');
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        title: '',
        phone_number: '',
        message: '',
    });

    useEffect(() => {
        const interval = window.setInterval(() => {
            setActiveHero((prev) => (prev + 1) % heroImages.length);
        }, 6000);
        return () => window.clearInterval(interval);
    }, [heroImages.length]);

    useEffect(() => {
        const nextImageIndex = (activeHero + 1) % heroImages.length;
        const preload = new window.Image();
        preload.src = heroImages[nextImageIndex];
    }, [activeHero, heroImages]);

    const submitNewsletter = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setNewsletterStatus('loading');
        setNewsletterError('');

        try {
            await api.post('/newsletter/subscriptions', {
                email: newsletterEmail.trim(),
                name: newsletterName.trim(),
                language: newsletterLanguage,
            });
            setNewsletterStatus('success');
            setNewsletterName('');
            setNewsletterEmail('');
        } catch (error: any) {
            setNewsletterStatus('error');
            const detail = error?.response?.data?.detail;
            if (typeof detail === 'string' && detail.toLowerCase().includes('already')) {
                setNewsletterError(t('home.newsletter.alreadySubscribed'));
            } else {
                setNewsletterError(detail || t('home.newsletter.error'));
            }
        }
    };

    const submitContact = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setContactStatus('loading');
        setContactError('');

        try {
            await createContactSubmission.mutateAsync({
                name: contactForm.name.trim(),
                email: contactForm.email.trim(),
                title: contactForm.title.trim(),
                phone_number: contactForm.phone_number.trim(),
                message: contactForm.message.trim(),
            });
            setContactStatus('success');
            setContactForm({
                name: '',
                email: '',
                title: '',
                phone_number: '',
                message: '',
            });
        } catch (error: any) {
            setContactStatus('error');
            setContactError(error?.response?.data?.detail || t('home.contact.error'));
        }
    };

    return (
        <div className="home">
            <Helmet>
                <title>{t('seo.home.title')}</title>
                <meta name="description" content={t('seo.home.description')} />
            </Helmet>
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-gallery">
                    <Image
                        key={heroImages[activeHero]}
                        src={heroImages[activeHero]}
                        alt={t('home.hero.title')}
                        fill
                        priority={activeHero === 0}
                        sizes="100vw"
                        className="hero-slide hero-slide-active"
                    />
                </div>
                <div className="hero-overlay" />
                <div className="container hero-content">
                    <h1 className="hero-title animate-fade-in">{t('home.hero.title')}</h1>
                    <p className="hero-subtitle animate-fade-in">{t('home.hero.subtitle')}</p>
                    <div className="hero-actions">
                        <Button size="lg" className="animate-fade-in" onClick={() => setIsNewsletterOpen(true)}>
                            {t('home.newsletter.cta')}
                        </Button>
                    </div>
                </div>
            </section>
            {isNewsletterOpen && (
                <div className="newsletter-overlay" role="dialog" aria-modal="true">
                    <div className="newsletter-card">
                        <button
                            type="button"
                            className="newsletter-close"
                            onClick={() => setIsNewsletterOpen(false)}
                            aria-label="Close newsletter form"
                        >
                            ×
                        </button>
                        <h3>{t('home.newsletter.title')}</h3>
                        <p>{t('home.newsletter.subtitle')}</p>
                        <form className="newsletter-form" onSubmit={submitNewsletter}>
                            <div className="newsletter-language">
                                <button
                                    type="button"
                                    className={`newsletter-lang-btn ${newsletterLanguage === 'EN' ? 'active' : ''}`}
                                    onClick={() => setNewsletterLanguage('EN')}
                                >
                                    EN
                                </button>
                                <button
                                    type="button"
                                    className={`newsletter-lang-btn ${newsletterLanguage === 'DE' ? 'active' : ''}`}
                                    onClick={() => setNewsletterLanguage('DE')}
                                >
                                    DE
                                </button>
                            </div>
                            <label className="newsletter-label" htmlFor="newsletter-name">
                                {t('home.newsletter.nameLabel')}
                            </label>
                            <input
                                id="newsletter-name"
                                type="text"
                                value={newsletterName}
                                onChange={(event) => setNewsletterName(event.target.value)}
                                placeholder={t('home.newsletter.namePlaceholder')}
                                className="newsletter-input"
                                required
                            />
                            <label className="newsletter-label" htmlFor="newsletter-email">
                                {t('home.newsletter.emailLabel')}
                            </label>
                            <input
                                id="newsletter-email"
                                type="email"
                                value={newsletterEmail}
                                onChange={(event) => setNewsletterEmail(event.target.value)}
                                placeholder={t('home.newsletter.emailPlaceholder')}
                                className="newsletter-input"
                                required
                            />
                            {newsletterStatus === 'error' && (
                                <p className="newsletter-error">{newsletterError}</p>
                            )}
                            {newsletterStatus === 'success' && (
                                <p className="newsletter-success">{t('home.newsletter.success')}</p>
                            )}
                            <Button type="submit" isLoading={newsletterStatus === 'loading'}>
                                {t('home.newsletter.submit')}
                            </Button>
                        </form>
                    </div>
                </div>
            )}

            {/* Services Section */}
            <section className="services">
                <div className="container">
                    <h2 className="section-title">{t('home.services.title')}</h2>
                    <p className="section-subtitle">{t('home.services.subtitle')}</p>

                    <div className="services-grid">
                        <Card glass hover className="service-card">
                            <MapPin size={48} className="service-icon" />
                            <h3>{t('home.services.marketEntry.title')}</h3>
                            <p>{t('home.services.marketEntry.description')}</p>
                        </Card>

                        <Card glass hover className="service-card">
                            <Building2 size={48} className="service-icon" />
                            <h3>{t('home.services.infrastructure.title')}</h3>
                            <p>{t('home.services.infrastructure.description')}</p>
                        </Card>

                        <Card glass hover className="service-card">
                            <Users size={48} className="service-icon" />
                            <h3>{t('home.services.consulting.title')}</h3>
                            <p>{t('home.services.consulting.description')}</p>
                        </Card>

                        <Card glass hover className="service-card">
                            <BookOpen size={48} className="service-icon" />
                            <h3>{t('home.services.knowledgeHub.title')}</h3>
                            <p>{t('home.services.knowledgeHub.description')}</p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Latest News */}
            <section className="latest-news">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">{t('home.latestNews')}</h2>
                        <Link href="/blog">
                            <Button variant="outline">{t('home.viewAll')}</Button>
                        </Link>
                    </div>

                    {isLoading ? (
                        <Loader />
                    ) : (
                        <div className="blog-grid">
                            {blogsData?.items.map((blog) => (
                                <Link
                                    key={blog.id}
                                    href={`/blog/${slugifyRouteValue(i18n.language === 'de' && blog.title_de ? blog.title_de : blog.title)}`}
                                >
                                    <Card hover className="blog-card">
                                        {blog.image && (
                                            <img
                                                src={resolveImageUrl(blog.image)}
                                                alt={i18n.language === 'de' && blog.title_de ? blog.title_de : blog.title}
                                                className="blog-image"
                                                loading="lazy"
                                                decoding="async"
                                                width={1200}
                                                height={675}
                                            />
                                        )}
                                        <div className="blog-content">
                                            <h3>{i18n.language === 'de' && blog.title_de ? blog.title_de : blog.title}</h3>
                                            {blog.Author && (
                                                <p className="blog-author">
                                                    {i18n.language === 'de' && blog.Author_de ? blog.Author_de : blog.Author}
                                                </p>
                                            )}
                                        </div>
                                    </Card>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Latest Knowledge Hub */}
            <section className="latest-news">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">{t('knowledgeHub.title')}</h2>
                        <Link href="/knowledge-hub">
                            <Button variant="outline">{t('home.viewAll')}</Button>
                        </Link>
                    </div>

                    {isKnowledgeLoading ? (
                        <Loader />
                    ) : (
                        <div className="blog-grid">
                            {knowledgeData?.items.map((article) => (
                                <Link
                                    key={article.id}
                                    href={`/knowledge-hub/${slugifyRouteValue(i18n.language === 'de' && article.title_de ? article.title_de : article.title)}`}
                                >
                                    <Card hover className="blog-card">
                                        {article.image && (
                                            <img
                                                src={resolveImageUrl(article.image)}
                                                alt={i18n.language === 'de' && article.title_de ? article.title_de : article.title}
                                                className="blog-image"
                                                loading="lazy"
                                                decoding="async"
                                                width={1200}
                                                height={675}
                                            />
                                        )}
                                        <div className="blog-content">
                                            <h3>{i18n.language === 'de' && article.title_de ? article.title_de : article.title}</h3>
                                            {article.topic && (
                                                <p className="blog-author">
                                                    {i18n.language === 'de' && article.topic_de ? article.topic_de : article.topic}
                                                </p>
                                            )}
                                        </div>
                                    </Card>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section className="contact-section">
                <div className="container contact-grid">
                    <div className="contact-info">
                        <h2>{t('home.contact.title')}</h2>
                        <p>{t('home.contact.subtitle')}</p>
                    </div>
                    <Card className="contact-card">
                        <form className="contact-form" onSubmit={submitContact}>
                            <div className="contact-form-row">
                                <div>
                                    <label className="contact-label" htmlFor="contact-name">
                                        {t('home.contact.nameLabel')}
                                    </label>
                                    <input
                                        id="contact-name"
                                        className="contact-input"
                                        type="text"
                                        value={contactForm.name}
                                        onChange={(event) =>
                                            setContactForm((prev) => ({ ...prev, name: event.target.value }))
                                        }
                                        placeholder={t('home.contact.namePlaceholder')}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="contact-label" htmlFor="contact-email">
                                        {t('home.contact.emailLabel')}
                                    </label>
                                    <input
                                        id="contact-email"
                                        className="contact-input"
                                        type="email"
                                        value={contactForm.email}
                                        onChange={(event) =>
                                            setContactForm((prev) => ({ ...prev, email: event.target.value }))
                                        }
                                        placeholder={t('home.contact.emailPlaceholder')}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="contact-form-row">
                                <div>
                                    <label className="contact-label" htmlFor="contact-title">
                                        {t('home.contact.titleLabel')}
                                    </label>
                                    <input
                                        id="contact-title"
                                        className="contact-input"
                                        type="text"
                                        value={contactForm.title}
                                        onChange={(event) =>
                                            setContactForm((prev) => ({ ...prev, title: event.target.value }))
                                        }
                                        placeholder={t('home.contact.titlePlaceholder')}
                                        required
                                    />
                                </div>
                                <div className="contact-phone">
                                    <label className="contact-label" htmlFor="contact-phone">
                                        {t('home.contact.phoneLabel')}
                                    </label>
                                    <PhoneInput
                                        defaultCountry="de"
                                        inputProps={{ id: 'contact-phone', required: true }}
                                        value={contactForm.phone_number}
                                        onChange={(value) =>
                                            setContactForm((prev) => ({ ...prev, phone_number: value }))
                                        }
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="contact-label" htmlFor="contact-message">
                                    {t('home.contact.messageLabel')}
                                </label>
                                <textarea
                                    id="contact-message"
                                    className="contact-textarea"
                                    value={contactForm.message}
                                    onChange={(event) =>
                                        setContactForm((prev) => ({ ...prev, message: event.target.value }))
                                    }
                                    placeholder={t('home.contact.messagePlaceholder')}
                                    rows={5}
                                    required
                                />
                            </div>
                            {contactStatus === 'error' && (
                                <p className="contact-error">{contactError}</p>
                            )}
                            {contactStatus === 'success' && (
                                <p className="contact-success">{t('home.contact.success')}</p>
                            )}
                            <Button type="submit" isLoading={contactStatus === 'loading'}>
                                {t('home.contact.submit')}
                            </Button>
                        </form>
                    </Card>
                </div>
            </section>
        </div>
    );
};
